import { useGetFindingsQuery, usePackageId, useProjectId } from '@consigli/hooks';
import { type Finding, FindingStatus } from '@consigli/types';
import { useMemo, useRef } from 'react';

import {
  rfpFindingsQuery,
  type RfpFindingsConflictType,
} from '@/pages/insight/dashboard/rfp/rfp-findings-query';

import { type UseAddContextListResult } from './use-add-context-list';

const noop = () => {};

/**
 * Active only, like the Add Findings list: a dismissed finding is not something
 * to ground an answer in. Filtered here rather than in the request, so the
 * request stays the dashboard's own and keeps sharing its cache.
 */
export const isOfferedRfpFinding = (finding: Finding): boolean =>
  finding.status === FindingStatus.ACTIVE;

/**
 * One RFP finding type for the add-context modal, narrowed to the active
 * findings `include` accepts and whose searchable text contains `search`.
 *
 * The request is the RFP dashboard's own, so RTK Query answers from the cache
 * the menu has already warmed. The whole type arrives in one response, so there
 * is no next page to scroll to.
 */
export const useRfpFindingsList = (
  conflictType: RfpFindingsConflictType,
  search: string,
  toSearchText: (finding: Finding) => string,
  include?: (finding: Finding) => boolean,
): UseAddContextListResult<Finding> => {
  const projectId = useProjectId();
  const packageId = usePackageId();
  const triggerRef = useRef<HTMLDivElement | null>(null);

  const { currentData, error, isLoading } = useGetFindingsQuery(
    rfpFindingsQuery(projectId, packageId, conflictType),
  );

  const items = useMemo(() => {
    const findings = (currentData?.results ?? []).filter(
      (finding) => isOfferedRfpFinding(finding) && (include?.(finding) ?? true),
    );
    const needle = search.trim().toLowerCase();
    if (!needle) return findings;
    return findings.filter((finding) => toSearchText(finding).toLowerCase().includes(needle));
  }, [currentData, include, search, toSearchText]);

  return {
    error,
    hasNextPage: false,
    isFetchingMore: false,
    isLoading,
    items,
    // The whole type arrives in one response, so there is no next page to ask for.
    onScroll: noop,
    triggerRef,
  };
};
